import { Dispatch } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import * as Sentry from '@sentry/react';
import { ExpectedError, reportError } from '../../lib/sentry';
import { AppDispatch } from '..';
import {
  IDiaryEntry,
  ICountUsersResponse,
  IUser,
  IAddUser,
  IGetPatientsQuery,
  IAddPatient,
} from '../../lib/interfaces';
import {
  sendAddNewPatientRequest,
  sendAddNewUserRequest,
  sendCountUsersRequest,
  sendGetDiaryEntriesRequest,
  sendGetPatientDetailsRequest,
  sendGetPatientsRequest,
  sendGetStatisticsFileRequest,
  sendUpdatePatientContactRequest,
  sendUpdateUserProfileRequest,
  getMyUserInfo,
} from '../../lib/services/generalService';
import type { ContactField } from '../../lib/services/generalService';
import { logoutSession, setAccessToken } from '../../lib/services/token';
import { assertAdminToken } from '../../lib/helpers/auth';
import i18n from '../../i18next';

export const LOGOUT = 'LOGOUT';
export const SET_IS_LOGGING_OUT = 'SET_IS_LOGGING_OUT';
export const LOGIN_STARTED = 'LOGIN_STARTED';
export const LOGIN_COMPLETED = 'LOGIN_COMPLETED';
export const LOGIN_FAILED = 'LOGIN_FAILED';
export const LOADER_TRUE = 'LOADER_TRUE';
export const LOADER_FALSE = 'LOADER_FALSE';
export const REFRESH_ADMIN_DATA = 'REFRESH_ADMIN_DATA';
export const GET_ALL_PATIENTS_STARTED = 'GET_ALL_PATIENTS_STARTED';
export const GET_ALL_PATIENTS_COMPLETED = 'GET_ALL_PATIENTS_COMPLETED';
export const GET_ALL_PATIENTS_FAILED = 'GET_ALL_PATIENTS_FAILED';
export const GET_PATIENT_DETAILS_STARTED = 'GET_PATIENT_DETAILS_STARTED';
export const GET_PATIENT_DETAILS_COMPLETED = 'GET_PATIENT_DETAILS_COMPLETED';
export const GET_PATIENT_DETAILS_FAILED = 'GET_PATIENT_DETAILS_FAILED';
export const UPDATE_USER_PROFILE_STARTED = 'UPDATE_USER_PROFILE_STARTED';
export const UPDATE_USER_PROFILE_COMPLETED = 'UPDATE_USER_PROFILE_COMPLETED';
export const UPDATE_USER_PROFILE_FAILED = 'UPDATE_USER_PROFILE_FAILED';
export const COUNT_USERS_STARTED = 'COUNT_USERS_STARTED';
export const COUNT_USERS_COMPLETED = 'COUNT_USERS_COMPLETED';
export const COUNT_USERS_FAILED = 'COUNT_USERS_FAILED';
export const GET_DIARY_PERIOD_STARTED = 'GET_DIARY_PERIOD_STARTED';
export const GET_DIARY_PERIOD_COMPLETED = 'GET_DIARY_PERIOD_COMPLETED';
export const GET_DIARY_PERIOD_FAILED = 'GET_DIARY_PERIOD_FAILED';
export const GET_DIARY_PERIOD_STATISTICS_STARTED =
  'GET_DIARY_PERIOD_STATISTICS_STARTED';
export const GET_DIARY_PERIOD_STATISTICS_COMPLETED =
  'GET_DIARY_PERIOD_STATISTICS_COMPLETED';
export const GET_DIARY_PERIOD_STATISTICS_FAILED =
  'GET_DIARY_PERIOD_STATISTICS_FAILED';
export const ADD_NEW_USER_STARTED = 'ADD_NEW_USER_STARTED';
export const ADD_NEW_PATIENT_STARTED = 'ADD_NEW_PATIENT_STARTED';
export const ADD_NEW_USER_COMPLETED = 'ADD_NEW_USER_COMPLETED';
export const ADD_NEW_USER_FAILED = 'ADD_NEW_USER_FAILED';
export const GET_STATISTICS_FILE_STARTED = 'GET_STATISTICS_FILE_STARTED';
export const GET_STATISTICS_FILE_COMPLETED = 'GET_STATISTICS_FILE_COMPLETED';
export const GET_STATISTICS_FILE_FAILED = 'GET_STATISTICS_FILE_FAILED';
export const SESSION_USER_FETCH_COMPLETED = 'SESSION_USER_FETCH_COMPLETED';
export const SESSION_USER_FETCH_FAILED = 'SESSION_USER_FETCH_FAILED';

interface Logout {
  type: typeof LOGOUT;
}
const logout = (): Logout => ({ type: LOGOUT });
interface SetIsLoggingOut {
  type: typeof SET_IS_LOGGING_OUT;
  payload: boolean;
}
const setIsLoggingOut = (isLoggingOut: boolean): SetIsLoggingOut => ({
  type: SET_IS_LOGGING_OUT,
  payload: isLoggingOut,
});
interface LoginStarted {
  type: typeof LOGIN_STARTED;
}
interface LoginCompleted {
  type: typeof LOGIN_COMPLETED;
  payload: Record<string, never>;
}
export const loginCompleted = (): LoginCompleted => ({
  type: LOGIN_COMPLETED,
  payload: {},
});
interface LoginFailed {
  type: typeof LOGIN_FAILED;
  payload: { error: string };
}
interface LoaderTrue {
  type: typeof LOADER_TRUE;
}
export const loaderTrue = (): LoaderTrue => ({ type: LOADER_TRUE });
interface LoaderFalse {
  type: typeof LOADER_FALSE;
}
export const loaderFalse = (): LoaderFalse => ({ type: LOADER_FALSE });
interface GetAllPatientsStarted {
  type: typeof GET_ALL_PATIENTS_STARTED;
}
export const getAllPatientsStarted = (): GetAllPatientsStarted => ({
  type: GET_ALL_PATIENTS_STARTED,
});
interface GetAllPatientsCompleted {
  type: typeof GET_ALL_PATIENTS_COMPLETED;
  payload: { patients: IUser[] };
}
export const getAllPatientsCompleted = (
  patients: IUser[]
): GetAllPatientsCompleted => ({
  type: GET_ALL_PATIENTS_COMPLETED,
  payload: { patients },
});
interface GetAllPatientsFailed {
  type: typeof GET_ALL_PATIENTS_FAILED;
  payload: { error: string };
}
export const getAllPatientsFailed = (error: string): GetAllPatientsFailed => ({
  type: GET_ALL_PATIENTS_FAILED,
  payload: { error },
});
interface GetPatientDetailsStarted {
  type: typeof GET_PATIENT_DETAILS_STARTED;
}
export const getPatientDetailsStarted = (): GetPatientDetailsStarted => ({
  type: GET_PATIENT_DETAILS_STARTED,
});
interface GetPatientDetailsCompleted {
  type: typeof GET_PATIENT_DETAILS_COMPLETED;
  payload: { patient: IUser };
}
export const getPatientDetailsCompleted = (
  patient: IUser
): GetPatientDetailsCompleted => ({
  type: GET_PATIENT_DETAILS_COMPLETED,
  payload: { patient },
});
interface GetPatientDetailsFailed {
  type: typeof GET_PATIENT_DETAILS_FAILED;
  payload: { error: string };
}
export const getPatientDetailsFailed = (
  error: string
): GetPatientDetailsFailed => ({
  type: GET_PATIENT_DETAILS_FAILED,
  payload: { error },
});
interface UpdateUserProfileStarted {
  type: typeof UPDATE_USER_PROFILE_STARTED;
}
export const updateUserProfileStarted = (): UpdateUserProfileStarted => ({
  type: UPDATE_USER_PROFILE_STARTED,
});
interface UpdateUserProfileCompleted {
  type: typeof UPDATE_USER_PROFILE_COMPLETED;
  payload: { updatedUser: IUser };
}
export const updateUserProfileCompleted = (
  updatedUser: IUser
): UpdateUserProfileCompleted => ({
  type: UPDATE_USER_PROFILE_COMPLETED,
  payload: { updatedUser },
});
interface UpdateUserProfileFailed {
  type: typeof UPDATE_USER_PROFILE_FAILED;
  payload: { error: string };
}
export const updateUserProfileFailed = (
  error: string
): UpdateUserProfileFailed => ({
  type: UPDATE_USER_PROFILE_FAILED,
  payload: { error },
});
interface CountUsersStarted {
  type: typeof COUNT_USERS_STARTED;
}
export const countUsersStarted = (): CountUsersStarted => ({
  type: COUNT_USERS_STARTED,
});
interface CountUsersCompleted {
  type: typeof COUNT_USERS_COMPLETED;
  payload: { usersCount: ICountUsersResponse };
}
export const countUsersCompleted = (
  usersCount: ICountUsersResponse
): CountUsersCompleted => ({
  type: COUNT_USERS_COMPLETED,
  payload: { usersCount },
});
interface CountUsersFailed {
  type: typeof COUNT_USERS_FAILED;
  payload: { error: string };
}
export const countUsersFailed = (error: string): CountUsersFailed => ({
  type: COUNT_USERS_FAILED,
  payload: { error },
});
interface GetDiaryPeriodStarted {
  type: typeof GET_DIARY_PERIOD_STARTED;
}
export const getDiaryPeriodStarted = (): GetDiaryPeriodStarted => ({
  type: GET_DIARY_PERIOD_STARTED,
});
interface GetDiaryPeriodCompleted {
  type: typeof GET_DIARY_PERIOD_COMPLETED;
  payload: {
    diaryEntries: IDiaryEntry[];
    startDate: Date | null;
    endDate: Date | null;
  };
}
export const getDiaryPeriodCompleted = (
  diaryEntries: IDiaryEntry[],
  startDate: Date | null,
  endDate: Date | null
): GetDiaryPeriodCompleted => ({
  type: GET_DIARY_PERIOD_COMPLETED,
  payload: { diaryEntries, startDate, endDate },
});
interface GetDiaryPeriodFailed {
  type: typeof GET_DIARY_PERIOD_FAILED;
  payload: { error: string };
}
export const getDiaryPeriodFailed = (error: string): GetDiaryPeriodFailed => ({
  type: GET_DIARY_PERIOD_FAILED,
  payload: { error },
});
interface GetDiaryPeriodStatisticsStarted {
  type: typeof GET_DIARY_PERIOD_STATISTICS_STARTED;
}
export const getDiaryPeriodStatisticsStarted =
  (): GetDiaryPeriodStatisticsStarted => ({
    type: GET_DIARY_PERIOD_STATISTICS_STARTED,
  });
interface GetDiaryPeriodStatisticsCompleted {
  type: typeof GET_DIARY_PERIOD_STATISTICS_COMPLETED;
  payload: { diaryEntries: IDiaryEntry[] };
}
export const getDiaryPeriodStatisticsCompleted = (
  diaryEntries: IDiaryEntry[]
): GetDiaryPeriodStatisticsCompleted => ({
  type: GET_DIARY_PERIOD_STATISTICS_COMPLETED,
  payload: { diaryEntries },
});
interface GetDiaryPeriodStatisticsFailed {
  type: typeof GET_DIARY_PERIOD_STATISTICS_FAILED;
  payload: { error: string };
}
export const getDiaryPeriodStatisticsFailed = (
  error: string
): GetDiaryPeriodStatisticsFailed => ({
  type: GET_DIARY_PERIOD_STATISTICS_FAILED,
  payload: { error },
});
interface AddNewUserStarted {
  type: typeof ADD_NEW_USER_STARTED;
}
interface AddNewPatientStarted {
  type: typeof ADD_NEW_PATIENT_STARTED;
}
export const addNewUserStarted = (): AddNewUserStarted => ({
  type: ADD_NEW_USER_STARTED,
});
export const addNewPatientStarted = (): AddNewPatientStarted => ({
  type: ADD_NEW_PATIENT_STARTED,
});
interface AddNewUserCompleted {
  type: typeof ADD_NEW_USER_COMPLETED;
  payload: { newAdmin: IUser };
}
export const addNewUserCompleted = (newAdmin: IUser): AddNewUserCompleted => ({
  type: ADD_NEW_USER_COMPLETED,
  payload: { newAdmin },
});
interface AddNewUserFailed {
  type: typeof ADD_NEW_USER_FAILED;
  payload: { error: string };
}
export const addNewUserFailed = (error: string): AddNewUserFailed => ({
  type: ADD_NEW_USER_FAILED,
  payload: { error },
});
interface GetStatisticsFileStarted {
  type: typeof GET_STATISTICS_FILE_STARTED;
}
export const getStatisticsFileStarted = (): GetStatisticsFileStarted => ({
  type: GET_STATISTICS_FILE_STARTED,
});
interface GetStatisticsFileCompleted {
  type: typeof GET_STATISTICS_FILE_COMPLETED;
}
export const getStatisticsFileCompleted = (): GetStatisticsFileCompleted => ({
  type: GET_STATISTICS_FILE_COMPLETED,
});
interface GetStatisticsFileFailed {
  type: typeof GET_STATISTICS_FILE_FAILED;
  payload: { error: string };
}
export const getStatisticsFileFailed = (
  error: string
): GetStatisticsFileFailed => ({
  type: GET_STATISTICS_FILE_FAILED,
  payload: { error },
});
interface SessionUserFetchCompleted {
  type: typeof SESSION_USER_FETCH_COMPLETED;
  payload: { user: IUser };
}
export const sessionUserFetchCompleted = (
  user: IUser
): SessionUserFetchCompleted => ({
  type: SESSION_USER_FETCH_COMPLETED,
  payload: { user },
});
interface SessionUserFetchFailed {
  type: typeof SESSION_USER_FETCH_FAILED;
  payload: { error: string };
}
export const sessionUserFetchFailed = (
  error: string
): SessionUserFetchFailed => ({
  type: SESSION_USER_FETCH_FAILED,
  payload: { error },
});

type ErrorLike = {
  response?: {
    data?: {
      result?: { message?: string };
      errors?: { msg?: string; message?: string }[];
    };
  };
  message?: string;
};

const extractErrorMessage = (
  err: unknown,
  defaultMessage: string = 'An unexpected error occurred.'
): string => {
  const error = err as ErrorLike | undefined;
  return (
    error?.response?.data?.result?.message ??
    error?.response?.data?.errors?.[0]?.msg ??
    error?.response?.data?.errors?.[0]?.message ??
    error?.message ??
    defaultMessage
  );
};

export const logoutAction =
  (navigate: (to: string) => void) =>
  async (
    dispatch: Dispatch,
    getState: () => { general: { isLoggingOut: boolean } }
  ): Promise<void> => {
    const state = getState();
    if (state.general.isLoggingOut) return;
    dispatch(setIsLoggingOut(true));
    dispatch(loaderTrue());

    try {
      await logoutSession();
    } catch (error) {
      reportError(error, { tags: { area: 'logout' }, level: 'warning' });
    }

    Sentry.setUser(null);
    sessionStorage.setItem('isLoggedOut', 'true');
    setAccessToken(null);
    localStorage.removeItem('jwt');
    localStorage.removeItem('profileImage');

    dispatch(loaderFalse());
    dispatch(logout());
    navigate('/login');
    dispatch(setIsLoggingOut(false));
  };

export const getAllPatients =
  (admins = false) =>
  (dispatch: Dispatch): Promise<void> => {
    dispatch(getAllPatientsStarted());
    const query: IGetPatientsQuery = admins
      ? { isAdmin: true }
      : { isPatient: true };

    return sendGetPatientsRequest(query)
      .then((res) => {
        if (res.data.status && res.data.result) {
          dispatch(getAllPatientsCompleted(res.data.result));
        } else {
          dispatch(getAllPatientsFailed('Failed to get patients'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(getAllPatientsFailed(extractErrorMessage(err)));
      });
  };

export const getPatientDetails =
  (id: string) =>
  (dispatch: Dispatch): void => {
    dispatch(getPatientDetailsStarted());

    sendGetPatientDetailsRequest(id)
      .then((res) => {
        if (res.data.status && res.data.result) {
          dispatch(getPatientDetailsCompleted(res.data.result));
        } else {
          dispatch(getPatientDetailsFailed('Failed to fetch patient details'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(getPatientDetailsFailed(extractErrorMessage(err)));
      });
  };

export const updateUserProfile =
  (property: string, data: File | string, id: string) =>
  (dispatch: Dispatch): void => {
    dispatch(updateUserProfileStarted());

    sendUpdateUserProfileRequest(property, data, id)
      .then((res) => {
        if (res.data.status && res.data.result?.user) {
          dispatch(updateUserProfileCompleted(res.data.result.user));
        } else {
          dispatch(updateUserProfileFailed('Failed to update profile'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(updateUserProfileFailed(extractErrorMessage(err)));
      });
  };

export const updatePatientContactDetails =
  (property: ContactField, data: string, id: string) =>
  (dispatch: Dispatch): void => {
    dispatch(updateUserProfileStarted());

    sendUpdatePatientContactRequest(property, data, id)
      .then((res) => {
        if (res.data.status && res.data.result?.user) {
          dispatch(updateUserProfileCompleted(res.data.result.user));
        } else {
          dispatch(
            updateUserProfileFailed(i18n.t('patientContact.updateFailed'))
          );
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(updateUserProfileFailed(extractErrorMessage(err)));
      });
  };

export const countUsers =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(countUsersStarted());

    sendCountUsersRequest()
      .then((res) => {
        if (res.data.status && res.data.result) {
          dispatch(countUsersCompleted(res.data.result));
        } else {
          dispatch(countUsersFailed('Failed to count users'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(countUsersFailed(extractErrorMessage(err)));
      });
  };

export const getDiaryEntries =
  (dateFrom: string, dateTo: string, userId: string) =>
  (dispatch: Dispatch): void => {
    dispatch(getDiaryPeriodStarted());

    sendGetDiaryEntriesRequest(dateFrom, dateTo, userId)
      .then((res) => {
        if (res.data.status && res.data.result) {
          dispatch(
            getDiaryPeriodCompleted(
              res.data.result,
              new Date(dateFrom),
              new Date(dateTo)
            )
          );
        } else {
          dispatch(getDiaryPeriodFailed('Failed to fetch diary entries'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(getDiaryPeriodFailed(extractErrorMessage(err)));
      });
  };

export const getDiaryEntriesStatistics =
  (dateFrom: string, dateTo: string, userId: string) =>
  (dispatch: Dispatch): void => {
    dispatch(getDiaryPeriodStatisticsStarted());

    sendGetDiaryEntriesRequest(dateFrom, dateTo, userId)
      .then((res) => {
        if (res.data.status && res.data.result) {
          dispatch(getDiaryPeriodStatisticsCompleted(res.data.result));
        } else {
          dispatch(
            getDiaryPeriodStatisticsFailed('Failed to fetch statistics')
          );
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(getDiaryPeriodStatisticsFailed(extractErrorMessage(err)));
      });
  };

export const getStatisticsFile =
  (dateFrom: string, dateTo: string, userId: string) =>
  (dispatch: Dispatch): void => {
    dispatch(getStatisticsFileStarted());

    sendGetStatisticsFileRequest(dateFrom, dateTo, userId)
      .then((res) => {
        const blob = res.data;
        if (blob) {
          saveAs(blob, `${userId}_statistics.pdf`);
          dispatch(getStatisticsFileCompleted());
        } else {
          dispatch(getStatisticsFileFailed('Failed to generate PDF'));
        }
      })
      .catch((err) => {
        reportError(err);
        dispatch(getStatisticsFileFailed(extractErrorMessage(err)));
      });
  };

export const addNewUser =
  (user: IAddUser) =>
  (dispatch: Dispatch): Promise<void> => {
    dispatch(addNewUserStarted());

    return sendAddNewUserRequest(user)
      .then((res) => {
        if (res.data.status && res.data.result?.user) {
          dispatch(addNewUserCompleted(res.data.result.user));
          const getAdmins = window.location.pathname.includes('admins');
          // @ts-expect-error - thunk passed to the plain Dispatch type
          dispatch(getAllPatients(getAdmins));
          return undefined;
        }
        const errorMessage = extractErrorMessage(res);
        dispatch(addNewUserFailed(errorMessage));

        return Promise.reject(new ExpectedError(errorMessage));
      })
      .catch((err) => {
        reportError(err);
        const errorMessage = extractErrorMessage(err);
        dispatch(addNewUserFailed(errorMessage));

        throw err;
      });
  };

export const addNewPatient =
  (user: IAddPatient) =>
  (dispatch: Dispatch): Promise<void> => {
    dispatch(addNewPatientStarted());

    return sendAddNewPatientRequest(user)
      .then((res) => {
        if (res.data.status && res.data.result?.user) {
          dispatch(addNewUserCompleted(res.data.result.user));
          const getAdmins = window.location.pathname.includes('admins');
          // @ts-expect-error - thunk passed to the plain Dispatch type
          dispatch(getAllPatients(getAdmins));
          return undefined;
        }
        const errorMessage = extractErrorMessage(res);

        dispatch(addNewUserFailed(errorMessage));

        return Promise.reject(new ExpectedError(errorMessage));
      })
      .catch((err) => {
        reportError(err);
        const errorMessage = extractErrorMessage(err);
        dispatch(addNewUserFailed(errorMessage));

        throw err;
      });
  };

export const performLogin =
  (token: string) => async (dispatch: AppDispatch) => {
    try {
      assertAdminToken(token);
      sessionStorage.removeItem('isLoggedOut');
      dispatch(loginCompleted());
      setAccessToken(token);

      const userResult = await getMyUserInfo();
      Sentry.setUser({ id: userResult._id?.toString() });
      dispatch(sessionUserFetchCompleted(userResult));
    } catch (error) {
      reportError(error, { tags: { area: 'login' } });
      dispatch(
        sessionUserFetchFailed(
          (error as Error).message || 'Failed to fetch user data'
        )
      );
      setAccessToken(null);
    }
  };

export type GeneralAction =
  | Logout
  | SetIsLoggingOut
  | LoginStarted
  | LoginCompleted
  | LoginFailed
  | LoaderTrue
  | LoaderFalse
  | GetAllPatientsStarted
  | GetAllPatientsCompleted
  | GetAllPatientsFailed
  | GetPatientDetailsStarted
  | GetPatientDetailsCompleted
  | GetPatientDetailsFailed
  | UpdateUserProfileStarted
  | UpdateUserProfileCompleted
  | UpdateUserProfileFailed
  | CountUsersStarted
  | CountUsersCompleted
  | CountUsersFailed
  | GetDiaryPeriodStarted
  | GetDiaryPeriodCompleted
  | GetDiaryPeriodFailed
  | GetDiaryPeriodStatisticsStarted
  | GetDiaryPeriodStatisticsCompleted
  | GetDiaryPeriodStatisticsFailed
  | AddNewUserStarted
  | AddNewUserCompleted
  | AddNewUserFailed
  | GetStatisticsFileStarted
  | GetStatisticsFileCompleted
  | GetStatisticsFileFailed
  | SessionUserFetchCompleted
  | SessionUserFetchFailed;
